import { z } from 'zod';
import { CargoNonnegMoneySchema } from './article.js';
import {
  CargoCarrierPaymentStatusSchema,
  CargoClientPaymentManualStatusSchema,
  CargoClientPaymentStatusSchema,
  CargoStatusSchema,
} from './enums.js';

// Cargo registry INPUT envelopes (#1228): list query, create, and update.
// The response projections live in `registry.ts`.

const IsoDateTime = z.string().datetime({ offset: true });

/**
 * @aiPurpose which date column the `dateFrom`/`dateTo` range filters against.
 *   `china_ship` → `timeFromChina`, `arrival` → `timeCargoArrivalToRf`,
 *   `issue` → `timeCargoRelease`. Defaults to `china_ship` (ship date is the
 *   primary registry ordering concept in the source).
 */
export const CargoDateTypeSchema = z.enum(['china_ship', 'arrival', 'issue']);
export type CargoDateType = z.infer<typeof CargoDateTypeSchema>;

/**
 * @aiPurpose overdue bucket for the two-phase issued-but-unpaid prefilter.
 *   `1plus` ≥ 1 day, `7plus` ≥ 7 days since issue without client payment.
 *   Absent ⇒ no overdue narrowing.
 */
export const CargoOverdueBucketSchema = z.enum(['1plus', '7plus']);
export type CargoOverdueBucket = z.infer<typeof CargoOverdueBucketSchema>;

/**
 * @aiPurpose collapsed lifecycle stage filter (#1884). `in_transit` →
 *   `status: in_transit`; `arrived_not_issued` → `status: arrived_rf` AND no
 *   release date; `issued` → has a release date (regardless of stored status,
 *   since issuance is derived from `timeCargoRelease`, not the status enum).
 *   Absent ⇒ no lifecycle narrowing. Lifecycle is the only active status-stage
 *   list filter.
 */
export const CargoLifecycleSchema = z.enum(['in_transit', 'arrived_not_issued', 'issued']);
export type CargoLifecycle = z.infer<typeof CargoLifecycleSchema>;

/**
 * @aiPurpose `GET /cargo` + `GET /cargo/summary` query contract (#1228).
 *   All business filters compose UNDER `AND` with the visibility predicate on
 *   the backend; none can widen the row scope. `.strict()` rejects unknown
 *   keys so a typo'd filter fails loudly instead of silently widening results.
 * @aiInvariant MVP excluded a «Выдан»/issued filter; that filter remains
 *   excluded. `pathFormat` is now an exact-match list filter mirroring `carrier`
 *   (#2164). Dirty quantity/cost range bounds are optional, inclusive, and
 *   nonnegative (#2581). `limit` is coerced + clamped to 1..100, default 50.
 */
export const CargoListQuerySchema = z
  .object({
    q: z.string().optional(),
    responsibleManagerId: z.string().uuid().optional(),
    carrier: z.string().trim().min(1).optional(),
    pathFormat: z.string().trim().min(1).optional(),
    clientPaymentStatus: CargoClientPaymentStatusSchema.optional(),
    carrierPaymentStatus: CargoCarrierPaymentStatusSchema.optional(),
    lifecycle: CargoLifecycleSchema.optional(),
    dateType: CargoDateTypeSchema.default('china_ship'),
    dateFrom: IsoDateTime.optional(),
    dateTo: IsoDateTime.optional(),
    weightFrom: z.coerce.number().nonnegative().optional(),
    weightTo: z.coerce.number().nonnegative().optional(),
    volumeFrom: z.coerce.number().nonnegative().optional(),
    volumeTo: z.coerce.number().nonnegative().optional(),
    totalCostFrom: z.coerce.number().nonnegative().optional(),
    totalCostTo: z.coerce.number().nonnegative().optional(),
    overdue: CargoOverdueBucketSchema.optional(),
    // #1939: restrict to cargos with ≥1 ACTIVE rejected client-payment document.
    // Only `true` narrows; absent/false applies no filter. `z.coerce.boolean()`
    // is unsafe for query strings (every non-empty string is truthy, incl.
    // "false"), so accept the literal "true"/"false" (or an already-parsed
    // boolean) and normalize, matching the catalog list schemas.
    hasRejectedDocument: z
      .union([z.boolean(), z.enum(['true', 'false'])])
      .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
      .optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict();
export type CargoListQuery = z.infer<typeof CargoListQuerySchema>;

// Dirty free-text quantity/money column — accepted verbatim (the migration and
// later issues tighten these). Optional + nullable so a patch can clear a field.
const DirtyTextInput = z.string().nullable();
const TrimmedArticle = z.string().trim().min(1, 'article must not be empty');

/**
 * @aiPurpose `POST /cargo` input (#1228). `.strict()` to reject stray keys.
 *   `article` is required + trimmed-non-empty; the backend derives
 *   `normalizedArticle` from it at write time. China-payment supply requires
 *   `cargo.chinaPayment.manage` (checked in the service, not here).
 */
export const CreateCargoArticleInputSchema = z
  .object({
    article: TrimmedArticle,
    responsibleManagerId: z.string().uuid().nullable().optional(),
    carrier: z.string().trim().min(1).nullable().optional(),
    pathFormat: z.string().trim().min(1).nullable().optional(),
    status: CargoStatusSchema.default('in_transit'),
    nameGoods: z.string().nullable().optional(),

    numberOfSeats: DirtyTextInput.optional(),
    weight: DirtyTextInput.optional(),
    volume: DirtyTextInput.optional(),
    transportationTariff: DirtyTextInput.optional(),
    transportationTariffForClients: DirtyTextInput.optional(),
    costGoods: DirtyTextInput.optional(),
    insuranceCost: DirtyTextInput.optional(),
    packagingCost: DirtyTextInput.optional(),
    totalCost: DirtyTextInput.optional(),
    addressTransportationCost: DirtyTextInput.optional(),
    prr: DirtyTextInput.optional(),
    tatCost: DirtyTextInput.optional(),

    chinaPaymentAmount: CargoNonnegMoneySchema.nullable().optional(),
    chinaWarehouseDispatchAmount: CargoNonnegMoneySchema.nullable().optional(),
    chinaWarehouseInspectionAmount: CargoNonnegMoneySchema.nullable().optional(),
    chinaWarehousePackagingAmount: CargoNonnegMoneySchema.nullable().optional(),
    paidByClientStatus: CargoClientPaymentManualStatusSchema.default('not_paid'),
    paymentToCarrierStatus: CargoCarrierPaymentStatusSchema.default('not_paid'),

    timeFromChina: IsoDateTime.nullable().optional(),
    timeCargoArrivalToRf: IsoDateTime.nullable().optional(),
    timeCargoRelease: IsoDateTime.nullable().optional(),
  })
  .strict();
export type CreateCargoArticleInput = z.infer<typeof CreateCargoArticleInputSchema>;

/**
 * @aiPurpose `PATCH /cargo/:id` input (#1228). `.strict()` + `nonEmptyPatch`
 *   (at least one key) so an empty body is a 400 rather than a no-op write.
 *   `chinaPaymentAmount` requires `cargo.chinaPayment.manage` and
 *   `paymentToCarrierStatus` requires `cargo.carrierPayment.manage`, both
 *   enforced in the service before the write. Setting an arrival/release date
 *   auto-derives `status` per the registry rules (also service-side).
 */
export const UpdateCargoArticleInputSchema = z
  .object({
    // `article` is the required identity; a patch may omit it, but when present
    // it must be non-empty (never null) — the service re-derives
    // `normalizedArticle` from it.
    article: TrimmedArticle.optional(),
    responsibleManagerId: z.string().uuid().nullable().optional(),
    carrier: z.string().trim().min(1).nullable().optional(),
    pathFormat: z.string().trim().min(1).nullable().optional(),
    status: CargoStatusSchema.optional(),
    nameGoods: z.string().nullable().optional(),
    numberOfSeats: DirtyTextInput.optional(),
    weight: DirtyTextInput.optional(),
    volume: DirtyTextInput.optional(),
    transportationTariff: DirtyTextInput.optional(),
    transportationTariffForClients: DirtyTextInput.optional(),
    costGoods: DirtyTextInput.optional(),
    insuranceCost: DirtyTextInput.optional(),
    packagingCost: DirtyTextInput.optional(),
    totalCost: DirtyTextInput.optional(),
    addressTransportationCost: DirtyTextInput.optional(),
    timeFromChina: IsoDateTime.nullable().optional(),
    chinaPaymentAmount: CargoNonnegMoneySchema.nullable().optional(),
    chinaWarehouseDispatchAmount: CargoNonnegMoneySchema.nullable().optional(),
    chinaWarehouseInspectionAmount: CargoNonnegMoneySchema.nullable().optional(),
    chinaWarehousePackagingAmount: CargoNonnegMoneySchema.nullable().optional(),
    prr: DirtyTextInput.optional(),
    tatCost: DirtyTextInput.optional(),
    paymentToCarrierStatus: CargoCarrierPaymentStatusSchema.optional(),
    timeCargoArrivalToRf: IsoDateTime.nullable().optional(),
    timeCargoRelease: IsoDateTime.nullable().optional(),
  })
  .strict()
  .refine((patch) => Object.keys(patch).length > 0, {
    message: 'patch must set at least one field',
  });
export type UpdateCargoArticleInput = z.infer<typeof UpdateCargoArticleInputSchema>;
